import { filterReadingMaterial } from "../reading-progression";
import type { ReadingSnapshot } from "@calega/content";
import { readingMaterialFor } from "@calega/content";
import type { SilbentrennungExerciseConfig, SilbentrennungRunSettings } from "@calega/content";
import { accuracy } from "../analysis-utils";
import { randomGenerator } from "../run-random";
import { normalizedItemText, pickRunItems, type RunSelection } from "../run-selection";

export interface SilbentrennungOrthographicUnit {
  text: string;
  start: number;
  end: number;
  unitType: "consonant" | "vowel" | "locked_cluster";
  clusterKind: string | null;
}

export interface SilbentrennungRunItem {
  id: string;
  surface: string;
  lemma: string;
  syllables: string[];
  expectedSplits: number[];
  expectedVowelPositions: number[];
  orthographicUnits: SilbentrennungOrthographicUnit[];
  wordFeatures: { containsLockedUnit: boolean; containsVcvBridge: boolean; containsVccvBridge: boolean };
}

export interface SilbentrennungRun {
  seed: string;
  generatorVersion: 1 | 2;
  settings: SilbentrennungRunSettings;
  items: SilbentrennungRunItem[];
}

const LESER_SILBEN_VOWELS = new Set(["a", "e", "i", "o", "u", "ä", "ö", "ü", "y"]);
const LESER_SILBEN_VOWEL_CLUSTERS = new Set(["ie", "ei", "ai", "au", "eu", "äu", "ae", "oe", "ue"]);
const LESER_SILBEN_CONSONANT_CLUSTERS = ["sch", "ch", "ck", "tz", "ph", "qu"];
const LESER_SILBEN_LOCKED_CLUSTERS = [...LESER_SILBEN_CONSONANT_CLUSTERS, ...LESER_SILBEN_VOWEL_CLUSTERS]
  .sort((left, right) => Array.from(right).length - Array.from(left).length);

function sameNumbers(left: number[], right: number[]) {
  return left.length === right.length && left.every((value, index) => value === right[index]);
}

function normalizedPositions(values: number[], max: number) {
  return [...new Set(values.filter((value) => Number.isInteger(value) && value >= 0 && value < max))].sort((left, right) => left - right);
}

function unitActsAsVowel(unit: SilbentrennungOrthographicUnit) {
  return unit.unitType === "vowel" || (unit.unitType === "locked_cluster" && LESER_SILBEN_VOWEL_CLUSTERS.has(unit.clusterKind ?? ""));
}

export function buildSilbentrennungOrthographicUnits(surface: string, readingSegments?: readonly string[]): SilbentrennungOrthographicUnit[] {
  const characters = Array.from(surface);
  const lower = Array.from(surface.toLocaleLowerCase("de-DE"));
  const units: SilbentrennungOrthographicUnit[] = [];
  const segmentsAt = new Map<number, string>();
  let segmentOffset = 0;
  for (const segment of readingSegments ?? []) {
    segmentsAt.set(segmentOffset, segment);
    segmentOffset += segment.startsWith("h:") ? 1 : Array.from(segment).length;
  }
  let index = 0;
  while (index < characters.length) {
    const cluster = LESER_SILBEN_LOCKED_CLUSTERS.find((candidate) => (!readingSegments || segmentsAt.get(index) === candidate) && lower.slice(index, index + Array.from(candidate).length).join("") === candidate);
    if (cluster) {
      const length = Array.from(cluster).length;
      units.push({ text: characters.slice(index, index + length).join(""), start: index, end: index + length, unitType: "locked_cluster", clusterKind: cluster });
      index += length;
      continue;
    }
    const character = characters[index]!;
    units.push({ text: character, start: index, end: index + 1, unitType: LESER_SILBEN_VOWELS.has(lower[index]!) ? "vowel" : "consonant", clusterKind: null });
    index += 1;
  }
  return units;
}

function silbentrennungFeatures(units: SilbentrennungOrthographicUnit[]) {
  const containsVcvBridge = units.some((middle, index) => index > 0 && index < units.length - 1
    && unitActsAsVowel(units[index - 1]!) && middle.unitType === "consonant" && Array.from(middle.text).length === 1 && unitActsAsVowel(units[index + 1]!));
  const containsVccvBridge = units.some((first, index) => index > 0 && index < units.length - 2
    && unitActsAsVowel(units[index - 1]!) && first.unitType === "consonant" && Array.from(first.text).length === 1
    && units[index + 1]!.unitType === "consonant" && Array.from(units[index + 1]!.text).length === 1 && unitActsAsVowel(units[index + 2]!));
  return { containsLockedUnit: units.some((unit) => unit.unitType === "locked_cluster"), containsVcvBridge, containsVccvBridge };
}

function syllableSplits(syllables: string[]) {
  const splits: number[] = [];
  let offset = 0;
  syllables.slice(0, -1).forEach((syllable) => { offset += Array.from(syllable).length; splits.push(offset); });
  return splits;
}

function vowelPositions(surface: string) {
  return Array.from(surface).flatMap((character, index) => LESER_SILBEN_VOWELS.has(character.toLocaleLowerCase("de-DE")) ? [index] : []);
}

export function formatSilbentrennung(surface: string, splits: number[]) {
  const characters = Array.from(surface);
  const boundaries = new Set(normalizedPositions(splits, characters.length).filter((position) => position > 0));
  return characters.map((character, index) => `${index > 0 && boundaries.has(index) ? "|" : ""}${character}`).join("");
}

export function generateSilbentrennungRun(config: SilbentrennungExerciseConfig, settings: SilbentrennungRunSettings, seed: string, reading?: ReadingSnapshot, selection?: RunSelection): SilbentrennungRun {
  const allowed = new Set(settings.syllableCounts);
  const candidates = filterReadingMaterial(config.words.filter((word) => allowed.has(Math.min(4, word.syllables.length) as 2 | 3 | 4)), (word) => word.surface, reading, config.readingMaterials);
  const random = randomGenerator(seed);
  const target = selection?.count ?? (settings.mode === "time" ? Math.max(120, settings.durationSeconds * 2) : settings.count);
  const selected = pickRunItems(candidates, target, random, (word) => `${normalizedItemText(word.surface)}|${word.syllables.map(normalizedItemText).join("|")}`, selection?.unique);
  return {
    seed,
    generatorVersion: 2,
    settings,
    items: selected.map((word, index) => {
      const orthographicUnits = buildSilbentrennungOrthographicUnits(word.surface, reading?.enabled ? readingMaterialFor(word.surface, config.readingMaterials)?.segments : undefined);
      return { id: `word-${index + 1}-${word.id}`, surface: word.surface, lemma: word.lemma, syllables: [...word.syllables], expectedSplits: syllableSplits(word.syllables), expectedVowelPositions: vowelPositions(word.surface), orthographicUnits, wordFeatures: silbentrennungFeatures(orthographicUnits) };
    }),
  };
}

export interface SilbentrennungItemAnalysis {
  correct: boolean;
  vowelCorrect: boolean;
  splitsCorrect: boolean;
  facts: {
    vowelLessSyllable: boolean;
    lockedUnitSplit: boolean;
    singleConsonantBoundLeft: boolean;
    consonantClusterNotSplit: boolean;
  };
}

export function analyzeSilbentrennungItem(item: SilbentrennungRunItem, vowelSelections: number[], selectedSplits: number[], vowelMarking: boolean): SilbentrennungItemAnalysis {
  const chosenVowels = normalizedPositions(vowelSelections, item.surface.length);
  const splits = normalizedPositions(selectedSplits, item.surface.length).filter((position) => position > 0);
  const vowelCorrect = !vowelMarking || sameNumbers(chosenVowels, item.expectedVowelPositions);
  const splitsCorrect = sameNumbers(splits, item.expectedSplits);
  const vowelLessSyllable = [0, ...splits, Array.from(item.surface).length].some((boundary, index, all) => index > 0 && !Array.from(item.surface).slice(all[index - 1]!, boundary).some((character) => LESER_SILBEN_VOWELS.has(character.toLocaleLowerCase("de-DE"))));
  const lockedUnitSplit = item.orthographicUnits.some((unit) => unit.unitType === "locked_cluster" && splits.some((position) => position > unit.start && position < unit.end));
  const singleConsonantBoundLeft = item.orthographicUnits.some((middle, index) => index > 0 && index < item.orthographicUnits.length - 1
    && unitActsAsVowel(item.orthographicUnits[index - 1]!) && middle.unitType === "consonant" && Array.from(middle.text).length === 1 && unitActsAsVowel(item.orthographicUnits[index + 1]!)
    && splits.includes(middle.end) && item.expectedSplits.includes(middle.start));
  const consonantClusterNotSplit = item.orthographicUnits.some((first, index) => index > 0 && index < item.orthographicUnits.length - 2
    && unitActsAsVowel(item.orthographicUnits[index - 1]!) && first.unitType === "consonant" && Array.from(first.text).length === 1
    && item.orthographicUnits[index + 1]!.unitType === "consonant" && Array.from(item.orthographicUnits[index + 1]!.text).length === 1 && unitActsAsVowel(item.orthographicUnits[index + 2]!)
    && (splits.includes(first.start) || splits.includes(item.orthographicUnits[index + 1]!.end)) && item.expectedSplits.includes(first.end));
  return { correct: vowelCorrect && splitsCorrect, vowelCorrect, splitsCorrect, facts: { vowelLessSyllable, lockedUnitSplit, singleConsonantBoundLeft, consonantClusterNotSplit } };
}

export interface SilbentrennungAnalysis {
  schemaVersion: "word-diagnostics-v1.1";
  total: number;
  answered: number;
  correct: number;
  accuracy: number | null;
  timeline: Array<{ order: number; itemKey: string; wordSurface: string; correct: boolean; facts: SilbentrennungItemAnalysis["facts"] }>;
  hypotheses: Array<{ ruleId: "silbentrennung.no_nucleus" | "silbentrennung.locked_unit_split" | "silbentrennung.single_consonant_left" | "silbentrennung.consonant_cluster_not_split"; status: "triggered" | "not_triggered" | "insufficient_data"; relevantCount: number; evidenceCount: number }>;
}

export function analyzeSilbentrennungRun(run: SilbentrennungRun, answers: Array<{ itemKey: string; stage: "vowels" | "splits"; vowelSelections: number[]; splits: number[] }>): SilbentrennungAnalysis {
  const latest = new Map<string, (typeof answers)[number]>();
  answers.filter((answer) => answer.stage === "splits").forEach((answer) => latest.set(answer.itemKey, answer));
  const resolved = run.items.flatMap((item) => {
    const answer = latest.get(item.id);
    return answer ? [{ item, analysis: analyzeSilbentrennungItem(item, answer.vowelSelections, answer.splits, run.settings.vowelMarking) }] : [];
  });
  const makeHypothesis = (ruleId: SilbentrennungAnalysis["hypotheses"][number]["ruleId"], relevant: typeof resolved, error: (entry: (typeof resolved)[number]) => boolean, minimum: number) => {
    const errors = relevant.filter(error).length;
    return { ruleId, relevantCount: relevant.length, evidenceCount: errors, status: relevant.length < minimum ? "insufficient_data" as const : errors >= 2 && errors / relevant.length >= .3 ? "triggered" as const : "not_triggered" as const };
  };
  const correct = resolved.filter(({ analysis }) => analysis.correct).length;
  return {
    schemaVersion: "word-diagnostics-v1.1",
    total: run.items.length,
    answered: resolved.length,
    correct,
    accuracy: accuracy(correct, resolved.length),
    timeline: resolved.map(({ item, analysis }, index) => ({ order: index + 1, itemKey: item.id, wordSurface: item.surface, correct: analysis.correct, facts: analysis.facts })),
    hypotheses: [
      makeHypothesis("silbentrennung.no_nucleus", resolved, ({ analysis }) => analysis.facts.vowelLessSyllable, 5),
      makeHypothesis("silbentrennung.locked_unit_split", resolved.filter(({ item }) => item.wordFeatures.containsLockedUnit), ({ analysis }) => analysis.facts.lockedUnitSplit, 3),
      makeHypothesis("silbentrennung.single_consonant_left", resolved.filter(({ item }) => item.wordFeatures.containsVcvBridge), ({ analysis }) => analysis.facts.singleConsonantBoundLeft, 3),
      makeHypothesis("silbentrennung.consonant_cluster_not_split", resolved.filter(({ item }) => item.wordFeatures.containsVccvBridge), ({ analysis }) => analysis.facts.consonantClusterNotSplit, 3),
    ],
  };
}
